import { useMutation } from "@tanstack/react-query";
import api, { apiFormData } from "../../utils/api";
import { useInvalidateDomain } from "../../lib/invalidation";
import { getError } from "../../utils/getError";
import type {
  CreateDeviceData,
  UpdateDeviceData,
  Device,
} from "../../store/admin/devicesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { fetchDevicesFromAPI } from "../../hooks/useVehiclesSocketSubscription";

function useInvalidateDevices() {
  const invalidateDomain = useInvalidateDomain();
  return () => {
    invalidateDomain("devices");
    fetchDevicesFromAPI(undefined, true).then((devices) => {
      if (devices) useVehiclesSocketStore.getState().refreshDevicesData(devices);
    });
  };
}

function useInvalidateAll() {
  const invalidateDomain = useInvalidateDomain();
  return () => {
    invalidateDomain("devices");
    invalidateDomain("vehicles");
    invalidateDomain("trailers");
    fetchDevicesFromAPI(undefined, true).then((devices) => {
      if (devices) useVehiclesSocketStore.getState().refreshDevicesData(devices);
    });
  };
}

export function useCreateDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({
    mutationFn: (data: Partial<CreateDeviceData>) =>
      api.post("/device", data).then((res) => res.data.data as Device),
    onSuccess: invalidate,
  });
}

export function useUpdateDevice() {
  const invalidate = useInvalidateDevices();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeviceData }) =>
      api
        .put("/device", data, { params: { id } })
        .then((res) => res.data.data as Device),
    onSuccess: invalidate,
  });
}

export function useUpdateDeviceStatus() {
  const invalidate = useInvalidateDevices();
  return useMutation({
    mutationFn: ({ id, statusId }: { id: string; statusId: string }) =>
      api
        .put("/deviceStatus", { status_id: statusId }, { params: { id } })
        .then((res) => res.data.data as Device),
    onSuccess: invalidate,
  });
}

export function useRefreshDeviceCache() {
  return useMutation({
    mutationFn: (id: string) =>
      api
        .post("/device/refreshCache", null, { params: { id } })
        .then((res) => res.data),
  });
}

export interface LocalDeviceLite {
  id: string;
  identifier: string | null;
  company_id: string | null;
  vehicle_id: string | null;
  remolque_id: string | null;
  brand_id: string | null;
  status_id: string | null;
  name: string | null;
  is_active: boolean | null;
}

export interface DeviceMetaSourceData {
  imei?: string;
  company_id?: string | null;
  vehicle_id?: string | null;
  remolque_id?: string | null;
  brand_id?: string | null;
  status_id?: string | null;
  is_active?: boolean;
  device_name?: string | null;
  vehicle_name?: string | null;
  remolque_name?: string | null;
}

export interface DeviceMetaSource {
  ok?: boolean;
  skipped?: boolean;
  device_id?: string;
  meta?: DeviceMetaSourceData;
  purged_old_device_id?: string | null;
}

export interface DeviceMetaInspectResult {
  imei: string;
  hasDuplicates: boolean;
  published: boolean;
  local: { devices: LocalDeviceLite[] };
  source: DeviceMetaSource | null;
}

function unwrapMeta(response: unknown): DeviceMetaInspectResult {
  const res = response as any;
  const body = res?.data;
  return (body?.data?.data ?? body?.data ?? body) as DeviceMetaInspectResult;
}

function inspectDeviceMeta(imei: string, publish: boolean) {
  return api
    .post("/device/meta/refresh", { imei, publish })
    .then(unwrapMeta);
}

export function useInspectDeviceMeta() {
  return useMutation({
    mutationFn: (imei: string) => inspectDeviceMeta(imei, false),
  });
}

export function useApplyDeviceMetaRefresh() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (imei: string) => inspectDeviceMeta(imei, true),
    onSuccess: invalidate,
  });
}

export function useDeleteDevice() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, cascade = true }: { id: string; cascade?: boolean }) =>
      api
        .delete("/device", { params: { id, cascade } })
        .then((res) => res.data),
    onSuccess: invalidate,
  });
}

export interface DeviceCreationPayload {
  device: Partial<CreateDeviceData>;
  vehicle?: {
    mode: "existing" | "new" | "none";
    id?: string;
    data?: Record<string, unknown>;
  };
  remolque?: {
    mode: "existing" | "new" | "none";
    id?: string;
    data?: FormData;
  };
}

function unwrapId(response: unknown): string {
  const res = response as any;
  const body = res?.data;        // { meta, data: <payload> }
  const lvl1 = body?.data;       // { message, data: entity } | entity
  const lvl2 = lvl1?.data;       // entity (cuando viene envuelto)
  return lvl2?.id ?? lvl1?.id ?? body?.id ?? "";
}

class DeviceCreationError extends Error {
  step: "vehicle" | "remolque" | "association" | "device";
  created: { vehicleId?: string; remolqueId?: string };

  constructor(
    message: string,
    step: "vehicle" | "remolque" | "association" | "device",
    created: { vehicleId?: string; remolqueId?: string },
  ) {
    super(message);
    this.name = "DeviceCreationError";
    this.step = step;
    this.created = created;
  }
}

async function preValidate(payload: DeviceCreationPayload): Promise<void> {
  const errors: string[] = [];

  if (payload.vehicle?.mode === "new" && payload.vehicle.data) {
    const plate = String(payload.vehicle.data.plate || "").trim();
    if (plate) {
      const check = await checkVehiclePlateAvailable(plate);
      if (!check.available) errors.push(check.message!);
    }
  }

  if (payload.remolque?.mode === "new" && payload.remolque.data) {
    const plate = payload.remolque.data.get?.("plate");
    if (plate && String(plate).trim()) {
      const check = await checkTrailerPlateAvailable(String(plate));
      if (!check.available) errors.push(check.message!);
    }
  }

  if (errors.length > 0) {
    throw new Error(errors.join(". "));
  }
}

async function executeDeviceCreation(payload: DeviceCreationPayload): Promise<Device> {
  await preValidate(payload);

  let vehicleId: string | undefined;
  let remolqueId: string | undefined;

  if (payload.vehicle?.mode === "existing" && payload.vehicle.id) {
    vehicleId = payload.vehicle.id;
  } else if (payload.vehicle?.mode === "new" && payload.vehicle.data) {
    try {
      const res = await api.post("/vehicle", payload.vehicle.data);
      vehicleId = unwrapId(res);
      if (!vehicleId) throw new Error("No se obtuvo ID del vehículo creado");
    } catch (err) {
      throw new DeviceCreationError(
        `Error al crear el vehículo: ${getError(err)}`,
        "vehicle",
        {},
      );
    }
  }

  // Paso 2: Resolver remolque
  if (payload.remolque?.mode === "existing" && payload.remolque.id) {
    remolqueId = payload.remolque.id;
  } else if (payload.remolque?.mode === "new" && payload.remolque.data) {
    try {
      const res = await apiFormData.post("/remolques", payload.remolque.data);
      remolqueId = unwrapId(res);
      if (!remolqueId) throw new Error("No se obtuvo ID del remolque creado");
    } catch (err) {
      throw new DeviceCreationError(
        `Error al crear el remolque: ${getError(err)}. ${vehicleId ? `El vehículo sí fue creado (ID: ${vehicleId}).` : ""}`,
        "remolque",
        { vehicleId },
      );
    }
  }

  // Paso 3: Asociar vehículo con remolque
  if (vehicleId && remolqueId) {
    try {
      await api.post("/associateOrRemoveRemolqueToVehicle", { vehicleId, remolqueId });
    } catch (err) {
      throw new DeviceCreationError(
        `Error al asociar vehículo con remolque: ${getError(err)}. Ambos fueron creados pero no están vinculados.`,
        "association",
        { vehicleId, remolqueId },
      );
    }
  }

  // Paso 4: Crear el dispositivo
  const deviceData = { ...payload.device };
  if (vehicleId) deviceData.vehicle_id = vehicleId;

  try {
    const res = await api.post("/device", deviceData);
    return (res.data?.data ?? res.data) as Device;
  } catch (err) {
    const parts: string[] = [`Error al crear el dispositivo: ${getError(err)}`];
    if (vehicleId && payload.vehicle?.mode === "new") {
      parts.push(`El vehículo fue creado (ID: ${vehicleId})`);
    }
    if (remolqueId && payload.remolque?.mode === "new") {
      parts.push(`El remolque fue creado (ID: ${remolqueId})`);
    }
    if (parts.length > 1) {
      parts.push("Puedes reintentar seleccionando 'Existente' para las entidades ya creadas.");
    }
    throw new DeviceCreationError(parts.join(". "), "device", { vehicleId, remolqueId });
  }
}

export function useCreateDeviceFlow() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: executeDeviceCreation,
    onSuccess: invalidate,
  });
}

export interface AvailabilityResult {
  available: boolean;
  message?: string;
}

export async function checkIdentifierAvailable(
  identifier: string,
): Promise<AvailabilityResult> {
  const trimmed = identifier.trim();
  if (!trimmed) return { available: true };

  try {
    const res = await api.get("/deviceByIdentifier", {
      params: { identifier: trimmed, checkOnly: true },
    });
    const sameCompany = res.data?.data?.data?.sameCompany;
    return {
      available: false,
      message:
        sameCompany === false
          ? "Este IMEI ya está registrado en otra compañía"
          : "Este IMEI ya está registrado en la base de datos",
    };
  } catch (err: any) {
    if (err?.response?.status === 404) return { available: true };
    return { available: true };
  }
}

export async function checkVehiclePlateAvailable(plate: string): Promise<AvailabilityResult> {
  const trimmed = plate.trim();
  if (!trimmed) return { available: true };

  try {
    const res = await api.get("/vehicleByPlate", {
      params: { plate: trimmed, checkOnly: true },
    });
    const sameCompany = res.data?.data?.data?.sameCompany;
    return {
      available: false,
      message:
        sameCompany === false
          ? "Esta placa ya está registrada en otra compañía"
          : "Esta placa ya está registrada en un vehículo",
    };
  } catch (err: any) {
    if (err?.response?.status === 404) return { available: true };
    return { available: true };
  }
}

export async function checkTrailerPlateAvailable(plate: string): Promise<AvailabilityResult> {
  const trimmed = plate.trim();
  if (!trimmed) return { available: true };

  try {
    const res = await api.get("/remolques", {
      params: { plate: trimmed, checkOnly: true },
    });
    const sameCompany = res.data?.data?.data?.sameCompany;
    return {
      available: false,
      message:
        sameCompany === false
          ? "Esta placa ya está registrada en otra compañía"
          : "Esta placa ya está registrada en un remolque",
    };
  } catch (err: any) {
    if (err?.response?.status === 404) return { available: true };
    return { available: true };
  }
}

export type TransferBackup =
  | { mode: "archive"; sources: string[] }
  | { mode: "purge" };

export type TransferStatusPolicy = "keep" | "active" | "inactive";

export interface TransferNamedRef {
  id: string;
  name: string | null;
}

export interface TransferSharedRemolque extends TransferNamedRef {
  devices: TransferNamedRef[];
  vehicles: TransferNamedRef[];
}

export interface TransferDrag {
  vehicle: TransferNamedRef | null;
  remolques: TransferNamedRef[];
  drivers: TransferNamedRef[];
  sharedRemolques: TransferSharedRemolque[];
}

export interface TransferPreview {
  deviceId: string;
  identifier: string | null;
  statusId: string | null;
  isActive: boolean;
  companyInSg: boolean;
  drag: TransferDrag;
}

export interface TransferUnassigned {
  geofences: TransferNamedRef[];
  truckYards: TransferNamedRef[];
  trips: TransferNamedRef[];
  groups: TransferNamedRef[];
  users: TransferNamedRef[];
  drivers: TransferNamedRef[];
}

export interface TransferResult {
  deviceId: string;
  transferId: string;
  from: string;
  to: string;
  backedUp: boolean;
  backupJobId: string | null;
  backupError: string | null;
  statusPolicy: TransferStatusPolicy;
  statusChanged: boolean;
  unassigned: TransferUnassigned;
  cleanupFailedCount: number;
  sgFailed: boolean;
  sharedRemolques: TransferSharedRemolque[];
}

export async function fetchTransferPreview(
  deviceId: string,
): Promise<TransferPreview> {
  const res = await api.get("/device-transfer/preview", {
    params: { id: deviceId },
  });
  return res.data?.data?.data;
}

export interface BulkTransferResult {
  total: number;
  transferred: number;
  failed: number;
  results: Array<{ deviceId: string; ok: boolean; error?: string }>;
}

export async function transferDevicesBulk(
  deviceIds: string[],
  toCompanyId: string,
  backup: TransferBackup,
  statusPolicy: TransferStatusPolicy = "keep",
): Promise<BulkTransferResult> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const res = await api.post("/device-transfer/bulk", {
    deviceIds,
    toCompanyId,
    backup,
    statusPolicy,
    timeZone,
  });
  return res.data?.data?.data;
}

export async function transferDevice(
  deviceId: string,
  toCompanyId: string,
  backup: TransferBackup,
  statusPolicy: TransferStatusPolicy = "keep",
): Promise<TransferResult> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const res = await api.post("/device-transfer", {
    deviceId,
    toCompanyId,
    backup,
    statusPolicy,
    timeZone,
  });
  return res.data?.data?.data;
}
